/**
 * Map and geographic utilities
 * Helpers for coordinate calculations, grid estimation, and map operations
 * Supports polygon filtering for freehand area selection
 */

import type { Bounds, Coordinates, GridConfig, GridPreview, GridCellDisplay, PolygonCoordinates } from '@/types/grid';
import booleanIntersects from '@turf/boolean-intersects';
import { polygon as turfPolygon } from '@turf/helpers';
import turfArea from '@turf/area';
import type { Feature, Polygon as GeoPolygon } from 'geojson';

/**
 * Earth radius in kilometers
 */
const EARTH_RADIUS_KM = 6371;

/**
 * Convert polygon coordinates to Turf.js polygon format
 */
function polygonToTurf(polygon: PolygonCoordinates): Feature<GeoPolygon> {
  // Ensure the polygon is closed (first point equals last point)
  const coords: [number, number][] = polygon.map(p => [p.lng, p.lat]);
  if (coords.length > 0) {
    const first = coords[0];
    const last = coords[coords.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) {
      coords.push([first[0], first[1]]);
    }
  }
  return turfPolygon([coords]);
}

/**
 * Convert cell bounds to Turf.js polygon format
 */
function boundsToTurf(bounds: Bounds): Feature<GeoPolygon> {
  // Create rectangle from bounds
  const coords: [number, number][] = [
    [bounds.west, bounds.south],
    [bounds.east, bounds.south],
    [bounds.east, bounds.north],
    [bounds.west, bounds.north],
    [bounds.west, bounds.south], // Close the ring
  ];
  return turfPolygon([coords]);
}

/**
 * Check if a cell intersects with a polygon
 * A cell is included if it overlaps with the polygon in any way
 */
function cellIntersectsPolygon(cellBounds: Bounds, polygon: Feature<GeoPolygon>): boolean {
  const cellPolygon = boundsToTurf(cellBounds);
  return booleanIntersects(cellPolygon, polygon);
}

/**
 * Convert degrees to radians
 */
export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Convert radians to degrees
 */
export function toDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Calculate distance between two points using Haversine formula
 */
export function calculateDistance(
  point1: Coordinates,
  point2: Coordinates
): number {
  const lat1 = toRadians(point1.lat);
  const lat2 = toRadians(point2.lat);
  const deltaLat = toRadians(point2.lat - point1.lat);
  const deltaLng = toRadians(point2.lng - point1.lng);

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) * Math.sin(deltaLng / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  return EARTH_RADIUS_KM * c;
}

/**
 * Calculate the center of bounds
 */
export function calculateCenter(bounds: Bounds): Coordinates {
  return {
    lat: (bounds.north + bounds.south) / 2,
    lng: (bounds.east + bounds.west) / 2,
  };
}

/**
 * Calculate area of bounds in square kilometers
 */
export function calculateArea(bounds: Bounds): number {
  const width = calculateDistance(
    { lat: bounds.south, lng: bounds.west },
    { lat: bounds.south, lng: bounds.east }
  );
  const height = calculateDistance(
    { lat: bounds.south, lng: bounds.west },
    { lat: bounds.north, lng: bounds.west }
  );
  return width * height;
}

/**
 * Calculate bounds dimensions in kilometers
 */
export function calculateBoundsDimensions(bounds: Bounds): { width: number; height: number } {
  const width = calculateDistance(
    { lat: bounds.south, lng: bounds.west },
    { lat: bounds.south, lng: bounds.east }
  );
  const height = calculateDistance(
    { lat: bounds.south, lng: bounds.west },
    { lat: bounds.north, lng: bounds.west }
  );
  return { width, height };
}

/**
 * Calculate optimal zoom level for cell size
 */
export function calculateOptimalZoom(cellSizeKm: number): number {
  // Approximate relationship between zoom and visible area
  // Zoom 16 ≈ 1km cell, Zoom 15 ≈ 2km cell, etc.
  if (cellSizeKm <= 0.5) return 17;
  if (cellSizeKm <= 1) return 16;
  if (cellSizeKm <= 2) return 15;
  if (cellSizeKm <= 4) return 14;
  return 13;
}

/**
 * Calculate cell size in kilometers for a zoom level
 * Must match backend's getRecommendedCellSizeKm in ZoomOptimizer.ts
 */
export function zoomToCellSize(zoom: number): number {
  // Approximate cell size at different zoom levels
  // These values must match the backend's getRecommendedCellSizeKm
  const zoomToCellMap: Record<number, number> = {
    13: 20,
    14: 10,
    15: 5,
    16: 2,
    17: 1,
    18: 0.5,
  };
  return zoomToCellMap[zoom] || 2;
}

/**
 * Convert cell size back to approximate zoom level
 * Must match backend's calculateZoomFromCellSizeKm in ZoomOptimizer.ts
 */
export function cellSizeToZoom(cellSizeKm: number): number {
  if (cellSizeKm >= 20) return 13;
  if (cellSizeKm >= 10) return 14;
  if (cellSizeKm >= 5) return 15;
  if (cellSizeKm >= 2) return 16;
  if (cellSizeKm >= 1) return 17;
  return 18;
}

/**
 * Calculate number of grid cells needed
 */
export function calculateGridCells(
  bounds: Bounds,
  cellSizeKm: number,
  overlapPercent: number = 10
): number {
  const { width, height } = calculateBoundsDimensions(bounds);

  // Adjust for overlap
  const effectiveCellSize = cellSizeKm * (1 - overlapPercent / 100);

  const cellsX = Math.ceil(width / effectiveCellSize);
  const cellsY = Math.ceil(height / effectiveCellSize);

  return cellsX * cellsY;
}

/**
 * Generate grid preview for bounds
 * When a polygon is provided, only cells that intersect the polygon are included
 *
 * @param bounds - Geographic bounds to divide
 * @param config - Grid configuration
 * @param polygon - Optional polygon coordinates to filter cells (for freehand selection)
 */
export function generateGridPreview(
  bounds: Bounds,
  config: GridConfig,
  polygon?: PolygonCoordinates
): GridPreview {
  const { cellSizeKm, overlapPercent } = config;
  const { width, height } = calculateBoundsDimensions(bounds);
  const areaKm2 = calculateArea(bounds);

  // Generate cell display data (filtered by polygon if provided)
  const result = generateGridCellsDisplay(bounds, cellSizeKm, overlapPercent, polygon);

  // Use the true total cell count (not limited by preview)
  const totalCells = result.totalCells;

  // Calculate grid dimensions (theoretical max before filtering)
  const effectiveCellSize = cellSizeKm * (1 - overlapPercent / 100);
  const columns = Math.max(1, Math.ceil(width / effectiveCellSize));
  const rows = Math.max(1, Math.ceil(height / effectiveCellSize));

  // Estimate places (rough approximation based on typical density)
  const avgPlacesPerCell = 50;
  const minPlaces = Math.round(totalCells * avgPlacesPerCell * 0.3);
  const maxPlaces = Math.round(totalCells * avgPlacesPerCell * 1.5);

  // Estimate time (assuming ~3-5 seconds per cell)
  const minTimeMs = totalCells * 3000;
  const maxTimeMs = totalCells * 5000;

  return {
    totalCells,
    rows,
    columns,
    estimatedPlaces: { min: minPlaces, max: maxPlaces },
    estimatedTime: { min: minTimeMs, max: maxTimeMs },
    areaKm2,
    cells: result.cells,
  };
}

/**
 * Result of grid cell generation including both visual cells and true count
 */
interface GridCellsResult {
  cells: GridCellDisplay[];
  totalCells: number; // True cell count (may be larger than cells.length due to preview limit)
  isLimited: boolean; // True if visual cells were limited for performance
}

/**
 * Generate grid cells for display on map
 * When a polygon is provided, only cells that intersect the polygon are included
 *
 * @param bounds - Geographic bounds to divide
 * @param cellSizeKm - Cell size in kilometers
 * @param overlapPercent - Overlap percentage between cells
 * @param polygon - Optional polygon coordinates to filter cells (for freehand selection)
 */
export function generateGridCellsDisplay(
  bounds: Bounds,
  cellSizeKm: number,
  overlapPercent: number = 10,
  polygon?: PolygonCoordinates
): GridCellsResult {
  const cells: GridCellDisplay[] = [];
  const { width, height } = calculateBoundsDimensions(bounds);

  // Convert polygon to Turf.js format if provided
  const turfPoly = polygon && polygon.length >= 3 ? polygonToTurf(polygon) : null;

  // Calculate effective cell size accounting for overlap
  const effectiveCellSize = cellSizeKm * (1 - overlapPercent / 100);

  // Calculate number of cells
  const cellsX = Math.ceil(width / effectiveCellSize);
  const cellsY = Math.ceil(height / effectiveCellSize);

  // Calculate degree per km at center latitude
  const centerLat = (bounds.north + bounds.south) / 2;
  const latDegPerKm = 1 / 111.32; // Roughly constant
  const lngDegPerKm = 1 / (111.32 * Math.cos(toRadians(centerLat)));

  // Cell size in degrees
  const cellLatDeg = cellSizeKm * latDegPerKm;
  const cellLngDeg = cellSizeKm * lngDegPerKm;
  const effectiveLatDeg = effectiveCellSize * latDegPerKm;
  const effectiveLngDeg = effectiveCellSize * lngDegPerKm;

  // Maximum cells to render visually (for performance)
  const maxPreviewCells = 1000;
  // Maximum cells to iterate through (to prevent UI freeze)
  const maxIterateCells = 10000;

  // Calculate true total cell count first (without polygon filtering, we estimate)
  const theoreticalTotalCells = cellsX * cellsY;

  // For very large grids, skip cell-by-cell iteration to prevent UI freeze
  if (theoreticalTotalCells > maxIterateCells) {
    // For polygon selections on large grids, estimate based on polygon area ratio
    let estimatedTotalCells = theoreticalTotalCells;
    if (turfPoly) {
      // Estimate: ratio of polygon area to bounds area * total cells
      const boundsAreaKm2 = width * height;
      const polyArea = turfArea(turfPoly) / 1_000_000; // m² to km²
      const areaRatio = Math.min(1, polyArea / boundsAreaKm2);
      estimatedTotalCells = Math.round(theoreticalTotalCells * areaRatio);
    }

    // Return single boundary cell for visual but report estimated count
    return {
      cells: [
        {
          id: 'preview',
          north: bounds.north,
          south: bounds.south,
          east: bounds.east,
          west: bounds.west,
          status: 'pending',
        },
      ],
      totalCells: estimatedTotalCells,
      isLimited: true,
    };
  }

  // Generate cells and count total (including those that exceed visual limit)
  let cellIndex = 0;
  let totalCellCount = 0;
  let limitReached = false;

  for (let y = 0; y < cellsY; y++) {
    for (let x = 0; x < cellsX; x++) {
      const cellSouth = bounds.south + y * effectiveLatDeg;
      const cellNorth = Math.min(cellSouth + cellLatDeg, bounds.north);
      const cellWest = bounds.west + x * effectiveLngDeg;
      const cellEast = Math.min(cellWest + cellLngDeg, bounds.east);

      const cellBounds: Bounds = {
        north: cellNorth,
        south: cellSouth,
        east: cellEast,
        west: cellWest,
      };

      // If polygon is provided, skip cells that don't intersect
      if (turfPoly && !cellIntersectsPolygon(cellBounds, turfPoly)) {
        continue;
      }

      // Count all cells (for true total)
      totalCellCount++;

      // Only add to visual array if under limit
      if (cells.length < maxPreviewCells) {
        cells.push({
          id: `cell-${cellIndex}`,
          north: cellNorth,
          south: cellSouth,
          east: cellEast,
          west: cellWest,
          status: 'pending',
        });
        cellIndex++;
      } else {
        limitReached = true;
      }
    }
  }

  return {
    cells,
    totalCells: totalCellCount,
    isLimited: limitReached,
  };
}

/**
 * Check if a point is within bounds
 */
export function isPointInBounds(point: Coordinates, bounds: Bounds): boolean {
  return (
    point.lat >= bounds.south &&
    point.lat <= bounds.north &&
    point.lng >= bounds.west &&
    point.lng <= bounds.east
  );
}

/**
 * Expand bounds by a percentage
 */
export function expandBounds(bounds: Bounds, percent: number): Bounds {
  const latDiff = bounds.north - bounds.south;
  const lngDiff = bounds.east - bounds.west;
  const latExpansion = (latDiff * percent) / 100;
  const lngExpansion = (lngDiff * percent) / 100;

  return {
    north: Math.min(90, bounds.north + latExpansion),
    south: Math.max(-90, bounds.south - latExpansion),
    east: Math.min(180, bounds.east + lngExpansion),
    west: Math.max(-180, bounds.west - lngExpansion),
  };
}

/**
 * Validate bounds are within valid ranges
 */
export function validateBounds(bounds: Bounds): boolean {
  return (
    bounds.north >= -90 &&
    bounds.north <= 90 &&
    bounds.south >= -90 &&
    bounds.south <= 90 &&
    bounds.east >= -180 &&
    bounds.east <= 180 &&
    bounds.west >= -180 &&
    bounds.west <= 180 &&
    bounds.north > bounds.south
  );
}

/**
 * Convert Leaflet LatLngBounds to our Bounds type
 */
export function leafletBoundsToBounds(latLngBounds: {
  getNorth: () => number;
  getSouth: () => number;
  getEast: () => number;
  getWest: () => number;
}): Bounds {
  return {
    north: latLngBounds.getNorth(),
    south: latLngBounds.getSouth(),
    east: latLngBounds.getEast(),
    west: latLngBounds.getWest(),
  };
}

/**
 * Convert our Bounds type to Leaflet bounds array format
 */
export function boundsToLeafletBounds(
  bounds: Bounds
): [[number, number], [number, number]] {
  return [
    [bounds.south, bounds.west],
    [bounds.north, bounds.east],
  ];
}

/**
 * Format bounds for display
 */
export function formatBounds(bounds: Bounds): string {
  return `${bounds.south.toFixed(4)}°S - ${bounds.north.toFixed(4)}°N, ${bounds.west.toFixed(4)}°W - ${bounds.east.toFixed(4)}°E`;
}

/**
 * Calculate the appropriate map zoom level to fit bounds
 */
export function calculateFitZoom(bounds: Bounds, mapWidth: number, mapHeight: number): number {
  const { width, height } = calculateBoundsDimensions(bounds);

  // Calculate zoom based on dimensions
  // This is an approximation - actual values depend on map projection
  const latZoom = Math.log2(mapHeight / (height * 111.32 * 0.01));
  const lngZoom = Math.log2(mapWidth / (width * 111.32 * 0.01));

  return Math.floor(Math.min(latZoom, lngZoom, 18));
}
